import { supabase } from '../lib/supabaseClient';

interface CalendarEvent {
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  attendees: string[];
  location?: string;
  leadId?: string;
}

export class ChatbotCalendarService {
  // Utwórz wydarzenie w kalendarzu
  async createEvent(event: CalendarEvent): Promise<string> {
    try {
      // Zapisz w bazie danych
      const { data, error } = await supabase
        .from('calendar_events')
        .insert({
          title: event.title,
          description: event.description,
          start_date: event.startDate.toISOString(),
          end_date: event.endDate.toISOString(),
          attendees: event.attendees,
          location: event.location,
          lead_id: event.leadId,
          created_from: 'chatbot'
        })
        .select()
        .single();

      if (error) throw error;

      // Generuj link do Google Calendar
      const googleCalendarUrl = this.generateGoogleCalendarLink(event);
      
      return googleCalendarUrl;
    } catch (error) {
      console.error('Błąd tworzenia wydarzenia:', error);
      throw error;
    }
  }

  // Sprawdź dostępność terminów
  async checkAvailability(date: Date, duration: number = 60): Promise<boolean> {
    const startTime = new Date(date);
    const endTime = new Date(date.getTime() + duration * 60 * 1000);

    // Sprawdź czy nie ma konfliktów
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .gte('start_date', startTime.toISOString())
      .lte('start_date', endTime.toISOString());

    if (error) {
      console.error('Błąd sprawdzania dostępności:', error);
      return false;
    }

    return data.length === 0;
  }

  // Znajdź wolne terminy
  async findAvailableSlots(
    startDate: Date,
    endDate: Date,
    duration: number = 60,
    workingHours = { start: 8, end: 17 }
  ): Promise<Date[]> {
    const slots: Date[] = [];
    const current = new Date(startDate);

    while (current < endDate) {
      const hour = current.getHours();
      
      // Sprawdź czy to godziny pracy
      if (hour >= workingHours.start && hour < workingHours.end) {
        // Sprawdź czy termin jest wolny
        if (await this.checkAvailability(current, duration)) {
          slots.push(new Date(current));
        }
      }

      // Przejdź do następnej godziny
      current.setHours(current.getHours() + 1);
    }

    return slots;
  }

  // Generuj link do Google Calendar
  private generateGoogleCalendarLink(event: CalendarEvent): string {
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      details: event.description,
      dates: `${this.formatDateForGoogle(event.startDate)}/${this.formatDateForGoogle(event.endDate)}`,
      location: event.location || '',
      guests: event.attendees.join(',')
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  // Formatuj datę dla Google Calendar
  private formatDateForGoogle(date: Date): string {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }

  // Parsuj wiadomość użytkownika w poszukiwaniu informacji o spotkaniu
  async parseEventFromMessage(message: string, context: any): Promise<Partial<CalendarEvent> | null> {
    const lowerMessage = message.toLowerCase();
    
    // Szukaj wskazówek dotyczących spotkania
    const meetingKeywords = ['spotkanie', 'umówić', 'wizyta', 'prezentacja', 'pokaz'];
    const hasMeetingIntent = meetingKeywords.some(keyword => lowerMessage.includes(keyword));
    
    if (!hasMeetingIntent) return null;

    // Próba wyciągnięcia daty
    const datePatterns = [
      /(\d{1,2})\s*(stycznia|lutego|marca|kwietnia|maja|czerwca|lipca|sierpnia|września|października|listopada|grudnia)/i,
      /jutro/i,
      /pojutrze/i,
      /w\s*(poniedziałek|wtorek|środę|czwartek|piątek)/i
    ];

    let suggestedDate = new Date();
    
    // Sprawdź jutro
    if (lowerMessage.includes('jutro')) {
      suggestedDate.setDate(suggestedDate.getDate() + 1);
    } else if (lowerMessage.includes('pojutrze')) {
      suggestedDate.setDate(suggestedDate.getDate() + 2);
    }

    // Sprawdź godzinę
    const timeMatch = lowerMessage.match(/(\d{1,2}):?(\d{0,2})/);
    if (timeMatch) {
      suggestedDate.setHours(parseInt(timeMatch[1]));
      if (timeMatch[2]) {
        suggestedDate.setMinutes(parseInt(timeMatch[2]));
      } else {
        suggestedDate.setMinutes(0);
      }
    }

    return {
      title: 'Spotkanie - PlexiSystem',
      description: `Spotkanie dotyczące: ${message}`,
      startDate: suggestedDate,
      endDate: new Date(suggestedDate.getTime() + 60 * 60 * 1000), // +1h
      attendees: context.leadInfo?.email ? [context.leadInfo.email] : [],
      leadId: context.leadInfo?.id
    };
  }

  // Formatuj propozycję spotkania dla użytkownika
  formatMeetingSuggestion(slots: Date[]): string {
    if (slots.length === 0) {
      return 'Niestety, nie znalazłem wolnych terminów w najbliższym czasie.';
    }

    let message = '📅 **Dostępne terminy spotkania:**\n\n';
    
    const groupedByDay: Record<string, Date[]> = {};
    
    // Grupuj po dniach
    slots.forEach(slot => {
      const day = slot.toLocaleDateString('pl-PL', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });
      
      if (!groupedByDay[day]) {
        groupedByDay[day] = [];
      }
      groupedByDay[day].push(slot);
    });

    // Formatuj wyjście
    Object.entries(groupedByDay).slice(0, 3).forEach(([day, times]) => {
      message += `**${day}:**\n`;
      times.slice(0, 3).forEach(time => {
        const timeStr = time.toLocaleTimeString('pl-PL', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
        message += `• ${timeStr}\n`;
      });
      message += '\n';
    });

    message += 'Który termin Ci odpowiada?';
    
    return message;
  }
}

export const chatbotCalendar = new ChatbotCalendarService();
